/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_DATETIME,
  DATE_TYPE_TIME,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_DISCOUNT,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_PROVIDER_COMMISSION,
  LISTING_UNIT_TYPES,
  propTypes,
} from '../../util/types';

import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemDiscountMaybe from './LineItemDiscountMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    timeZone,
    currency,
    marketplaceName,
    intl,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const lineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));
  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, fixed, item
  const lineItemUnitType = unitLineItem?.code;
  const dateType = DATE_TYPE_DATE;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  const listingPublicData = transaction?.listing?.attributes?.publicData || {};
  const protectedData = transaction?.attributes?.protectedData || {};
  const metadata = transaction?.attributes?.metadata || {};

  const isMultipleDay =
    listingPublicData.experience_type === 'multiple_day' ||
    listingPublicData.experienceType === 'multiple_day' ||
    protectedData.experience_type === 'multiple_day' ||
    protectedData.experienceType === 'multiple_day';

  const secondInstallmentStatus =
    metadata?.secondInstallmentStatus === 'paid' ||
    metadata?.installments?.secondInstallmentStatus === 'paid' ||
    protectedData?.paymentInstallment === 'second';

  const thirdInstallmentStatus =
    metadata?.thirdInstallmentStatus === 'paid' ||
    metadata?.installments?.thirdInstallmentStatus === 'paid' ||
    protectedData?.paymentInstallment === 'third';

  const payinTotal = transaction?.attributes?.payinTotal;
  const isChildTx = protectedData?.isChildTransaction || protectedData?.childTransaction;

  let secondInstallmentMoney = null;
  let thirdInstallmentMoney = null;
  let discountMoney = null;

  const discountLineItem = lineItems.find(
    item => item.code === LINE_ITEM_DISCOUNT && !item.reversal
  );

  const unitPurchase = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );

  if (payinTotal) {
    const currency = payinTotal.currency;
    const baseFirstInstallmentAmt = unitPurchase?.lineTotal?.amount || 0;
    const discountFirstInstallmentAmt = Math.abs(
      discountLineItem?.lineTotal?.amount ??
      (discountLineItem?.unitPrice?.amount
        ? discountLineItem.unitPrice.amount * (discountLineItem.quantity || -1)
        : 0)
    );

    // If this is a conversation installment booking with a promotional discount applied
    if (discountLineItem && baseFirstInstallmentAmt > 0) {
      if (thirdInstallmentStatus && !isChildTx) {
        // All installments paid:
        // 1st installment gross: baseFirstInstallmentAmt ($300.00)
        // 2nd installment gross: 40% of package ($400.00)
        // 3rd installment gross: 30% of package ($300.00)
        // Full discount: 20% of package ($200.00)
        // Total price: $300 + $400 + $300 - $200 = $800.00
        const grossSecondAmt = Math.round((baseFirstInstallmentAmt / 3) * 4);
        const grossThirdAmt = baseFirstInstallmentAmt;
        const fullDiscountAmt = Math.round((discountFirstInstallmentAmt / 3) * 10);

        secondInstallmentMoney = new Money(grossSecondAmt, currency);
        thirdInstallmentMoney = new Money(grossThirdAmt, currency);
        discountMoney = new Money(fullDiscountAmt, currency);
      } else if (secondInstallmentStatus && !isChildTx) {
        // First and second installments paid
        const grossSecondAmt = Math.round((baseFirstInstallmentAmt / 3) * 4);
        const fullDiscountAmt = Math.round((discountFirstInstallmentAmt / 3) * 7);

        secondInstallmentMoney = new Money(grossSecondAmt, currency);
        discountMoney = new Money(fullDiscountAmt, currency);
      } else {
        discountMoney = new Money(discountFirstInstallmentAmt, currency);
      }
    } else {
      const secondAmt =
        metadata?.secondInstallmentAmount ||
        metadata?.installments?.secondInstallmentAmount ||
        Math.round((payinTotal.amount / 3) * 4);
      const thirdAmt =
        metadata?.thirdInstallmentAmount ||
        metadata?.installments?.thirdInstallmentAmount ||
        payinTotal.amount;

      secondInstallmentMoney = new Money(secondAmt, currency);
      thirdInstallmentMoney = new Money(thirdAmt, currency);
    }
  }

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />

      <LineItemBasePriceMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        isMultipleDay={isMultipleDay}
        transaction={transaction}
        intl={intl}
      />
      {secondInstallmentStatus && !isChildTx && secondInstallmentMoney ? (
        <div className={classNames(css.lineItem, css.bookingPeriod,css.secondInstallment)}>
          <span className={css.itemLabel}>
            <FormattedMessage
              id="OrderBreakdown.secondInstallment"
              defaultMessage="2nd Instalment (40%)"
            />
          </span>
          <span className={css.itemValue}>{formatMoney(intl, secondInstallmentMoney)}</span>
        </div>
      ) : null}
      {thirdInstallmentStatus && !isChildTx && thirdInstallmentMoney ? (
        <div className={classNames(css.lineItem, css.bookingPeriod,css.thirdInstallment)}>
          <span className={css.itemLabel}>
            <FormattedMessage
              id="OrderBreakdown.thirdInstallment"
              defaultMessage="3rd Instalment (30%)"
            />
          </span>
          <span className={css.itemValue}>{formatMoney(intl, thirdInstallmentMoney)}</span>
        </div>
      ) : null}
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemDiscountMaybe
        lineItems={lineItems}
        discountMoney={discountMoney}
        discountCode={
          protectedData?.discountCode ||
          metadata?.discountCode ||
          protectedData?.orderData?.discountCode ||
          'RR20'
        }
        intl={intl}
      />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
      />
      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

/**
 * Order breakdown aka receipt
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.marketplaceName
 * @param {string} props.timeZone IANA time zone name
 * @param {string} props.currency E.g. 'USD'
 * @param {'customer' | 'provider'} props.userRole
 * @param {propTypes.transaction} props.transaction
 * @param {propTypes.booking?} props.booking
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @returns {JSX.Element} the order breakdown component
 */
const OrderBreakdown = props => {
  const intl = useIntl();
  return <OrderBreakdownComponent intl={intl} {...props} />;
};

export default OrderBreakdown;
